import { Button, Popover, PopoverContent, PopoverTrigger } from '@playcastdotio/Shadcn';
import { Link, useLocation } from 'react-router-dom';
import { download, setAutoDownloadFlag, checkAndClearAutoDownloadFlag, clearAutoDownloadFlag, sendDownloadReminderEmail } from '../../utility/api';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWindows } from '@fortawesome/free-brands-svg-icons';
import config from '../../config.json';
import { faCircleUser, faComputer, faLock, faUser } from '@fortawesome/pro-solid-svg-icons';
import { SignedIn, SignedOut, SignInButton, UserButton, useUser, useClerk } from '@clerk/clerk-react';
import { User } from 'lucide-react';
import { faUserCog } from '@fortawesome/pro-solid-svg-icons';
import { Privacy } from '@playcastdotio/Settings';
import { usePavilion } from '../contexts/PavilionContext';
import { ownerAliasState } from '../../api/stores/lobbyStores';
import { IdentityPlatform } from '@playcastdotio/CoreTypes';
import { ProfileGetAliasesResponsePayload } from '@playcastdotio/Messaging';
import { determineProfileSlug, determinePlatformFromAliases } from '@playcastdotio/SharedComponents';
import constants from '@playcastdotio/GlobalConfig';
import { isMobileDevice, isNonWindowsOS } from '../../utility/utility';
import { toast } from 'sonner';

type ProfileNavProps = {
  name: string;
  handler?: () => void;
  url?: string;
};

export const Header = () => {
  const { user } = useUser();
  const { isPavilionMode } = usePavilion();
  const { openSignIn } = useClerk();
  const location = useLocation();

  const [ownerAliasPlatformPrefix, setOwnerAliasPlatformPrefix] = useState<'pt' | 'p' | 'pd' | undefined>(undefined);
  const [ownerAlias, setOwnerAlias] = useState<string | undefined>(undefined);
  const [hasAutoDownloadFlag, setHasAutoDownloadFlag] = useState(false);

  // Check if we're on a player profile page
  const isOnProfilePage = useMemo(() => {
    return location.pathname.startsWith('/p/') ||
           location.pathname.startsWith('/pt/') ||
           location.pathname.startsWith('/pd/');
  }, [location.pathname]);

  // Generate Play Now URL using the same logic as outbound service
  const playNowUrl = (() => {
    return constants.PAVILION_PLAYER_BASE_URL + 'p/playcast/';
  })();

  const getProfileUrl = useCallback(() => {
    if (!user) return undefined;

    const ownerAliases = ownerAliasState.currentState.aliases;

    // Determine platform using the utility function
    const platform = determinePlatformFromAliases(ownerAliases) || IdentityPlatform.CLERK;

    const profileSlug = determineProfileSlug({
      authId: user.id,
      username: user.username ?? '',
      defaultAlias: ownerAliases.default,
      platform: platform,
    });

    let baseUrl = constants.PAVILION_PLAYER_BASE_URL;
    baseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;

    return profileSlug ? `${baseUrl}${profileSlug}` : undefined;
  }, [user]);

  const profileUrl = useMemo(() => getProfileUrl(), [getProfileUrl, ownerAliasState.currentState]);

  useEffect(() => {
    const listener = ownerAliasState.addListener((state) => {
      const platform = determinePlatformFromAliases(state.aliases);
      if (platform) {
        switch (platform) {
          case IdentityPlatform.CLERK:
            setOwnerAliasPlatformPrefix('p');
            break;
          case IdentityPlatform.DISCORD:
            setOwnerAliasPlatformPrefix('pd');
            break;
          case IdentityPlatform.TWITCH:
            setOwnerAliasPlatformPrefix('pt');
            break;
        }
        setOwnerAlias(state.aliases[platform]);
      }
    });
    const platform = determinePlatformFromAliases(ownerAliasState.currentState.aliases);
    if (platform) {
      switch (platform) {
        case IdentityPlatform.CLERK:
          setOwnerAliasPlatformPrefix('p');
          break;
        case IdentityPlatform.DISCORD:
          setOwnerAliasPlatformPrefix('pd');
          break;
        case IdentityPlatform.TWITCH:
          setOwnerAliasPlatformPrefix('pt');
          break;
      }
      setOwnerAlias(ownerAliasState.currentState.aliases[platform]);
    }

    return () => {
      ownerAliasState.removeListener(listener);
    };
  }, []);

  // Check for auto-download flag on component load
  useEffect(() => {
    console.log('[Header] Auto-download check - user:', !!user);
    if (!user) {
      return;
    }

    // Add a small delay to ensure user object is fully loaded
    const timeoutId = setTimeout(async () => {
      const autoDownloadData = checkAndClearAutoDownloadFlag();
      console.log('[Header] Auto-download data:', autoDownloadData);
      if (autoDownloadData) {
        // Check if this was from a mobile device or non-Windows OS
        // isMobile flag is set to true for both mobile devices and non-Windows OS
        if (autoDownloadData.isMobile) {
          console.log('[Header] Mobile/Non-Windows detected, sending email');
          // On mobile or non-Windows OS, send email reminder instead of downloading
          const email = user.primaryEmailAddress?.emailAddress;
          if (email) {
            await sendDownloadReminderEmail(email);
            toast.success('Download reminder email sent!', {
              description: 'Check your email for a link to download Playcast on your PC.',
              duration: 5000,
              style: {
                background: '#fbbf24',
                color: '#000000',
                border: 'none',
              },
            });
          } else {
            toast.error('Unable to send reminder', {
              description: 'Email address not found.',
              style: {
                background: '#fbbf24',
                color: '#000000',
                border: 'none',
              },
            });
          }
        } else {
          console.log('[Header] Windows desktop detected, downloading');
          // On Windows desktop, proceed with normal download
          download();
        }
      }
    }, 100); // 100ms delay

    return () => clearTimeout(timeoutId);
  }, [user]);

  // Handle sign-in modal close to clear auto download flag
  useEffect(() => {
    const handleModalClose = () => {
      // If the modal was closed and user is still not signed in, clear the flag
      if (!user && hasAutoDownloadFlag) {
        clearAutoDownloadFlag();
        setHasAutoDownloadFlag(false);
      }
    };

    // Listen for clicks outside modal
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is on modal backdrop or outside modal
      if (target.classList.contains('cl-modal-backdrop') ||
          target.classList.contains('cl-modal-overlay') ||
          target.getAttribute('data-clerk-modal') === 'true') {
        setTimeout(handleModalClose, 100);
      }
    };

    // Listen for escape key
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && hasAutoDownloadFlag && !user) {
        setTimeout(handleModalClose, 100);
      }
    };

    // Listen for DOM changes that indicate modal close
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          // Check if Clerk modal elements were removed
          const removedNodes = Array.from(mutation.removedNodes);
          const modalClosed = removedNodes.some((node) => {
            if (node instanceof HTMLElement) {
              // Look for various Clerk modal selectors
              return node.classList.contains('cl-modal') ||
                     node.classList.contains('cl-modal-backdrop') ||
                     node.classList.contains('cl-modal-overlay') ||
                     node.querySelector('[data-clerk-modal]') ||
                     node.querySelector('.cl-modal') ||
                     node.querySelector('[role="dialog"]') ||
                     node.id?.includes('clerk') ||
                     node.className?.includes('clerk');
            }
            return false;
          });

          if (modalClosed && !user && hasAutoDownloadFlag) {
            setTimeout(handleModalClose, 100);
          }
        }
      });
    });

    // Start observing the document body for modal changes
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Add event listeners
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      observer.disconnect();
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [user, hasAutoDownloadFlag]);

  // Clear auto download flag state when user signs in successfully
  // Note: We don't clear the localStorage flag here because checkAndClearAutoDownloadFlag will handle that
  useEffect(() => {
    if (user && hasAutoDownloadFlag) {
      setHasAutoDownloadFlag(false);
    }
  }, [user, hasAutoDownloadFlag]);

  const handleDownloadClick = useCallback(() => {
    setAutoDownloadFlag(isMobileDevice() || isNonWindowsOS());
    setHasAutoDownloadFlag(true);
  }, []);

  const links = isPavilionMode
    ? [
        { name: 'HOME', url: '/' },
        { name: 'FAQs', url: '/faqs' },
      ]
    : [
        { name: 'Discord', url: '/discord' },
        { name: 'News', url: '/news' },
        { name: 'About', url: '/about-us' },
        { name: 'FAQs', url: '/faqs' },
      ];

  useEffect(() => {
    const links = document.querySelectorAll('#mobile-menu-links a');
    const checkbox = document.querySelector('#mobile-menu') as HTMLInputElement;

    links.forEach((link) => {
      link.addEventListener('click', () => {
        if (checkbox) {
          checkbox.checked = false;
        }
      });
    });

    return () => {
      links.forEach((link) => {
        link.removeEventListener('click', () => {
          checkbox.checked = false;
        });
      });
    };
  }, []);

  return isPavilionMode ? (
    <>
      {/* Desktop header */}
      <header className='text-header-nav sticky top-0 z-[99] m-0 hidden w-full bg-gray-900 align-middle md:flex'>
        <Link to='/' id='logo' className='mr-2 flex flex-row gap-x-2 p-4 align-middle lg:mr-16'>
          <img src='/pav-logo.png' alt='Playcast Pavilion' className='h-12 w-12' />
          <img src='/pav-title.png' alt='Playcast Pavilion' className='mt-2 hidden h-8 lg:block' />
        </Link>

        <nav id='large menu' className='flex flex-grow gap-x-4 align-middle'>
          <ul className='flex flex-row items-center gap-x-8 text-xl lg:text-2xl xl:text-3xl'>
            {links.map((link) => (
              <li key={link.name} className='text-header-nav hover:text-header-nav focus:text-header-nav active:text-header-nav'>
                <Link to={link.url}>{link.name}</Link>
              </li>
            ))}
          </ul>

          {/* Right-aligned group: Discord, Steam, User Icon */}
          <div className='ml-auto mr-8 flex h-full items-center gap-x-6'>
            {/* Discord Button */}
            {/* <a href='https://discord.com/invite/playcast' target='_blank' rel='noopener noreferrer'>
              <div className='box-border flex h-full w-[190px] cursor-pointer items-center justify-center bg-[#5865F2] px-[13px] py-[23px] transition hover:opacity-90'>
                <img src='/Discord-Logo-Light-Blurple.svg' className='h-[28.95px] w-[163px]' alt='Discord' />
              </div>
            </a> */}

            {/*Play Now Button*/}
            {!isOnProfilePage ? (
              <Link to={`${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}/p/playcast/`}>
                <Button className='flex !h-auto h-full w-[189px] items-center justify-center bg-transparent !p-0 hover:bg-transparent focus:bg-transparent focus-visible:bg-transparent active:bg-transparent'>
                  <img src='/PLAY NOW.svg' className='h-full w-full object-contain transition hover:opacity-90' alt='PLAY' />
                </Button>
              </Link>
            ) : (
              <Link to={`${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}/a/Playcast`}>
                <Button className='flex !h-auto h-full w-[189px] items-center justify-center bg-transparent !p-0 hover:bg-transparent focus:bg-transparent focus-visible:bg-transparent active:bg-transparent'>
                  <img src='/JOIN PAVILION.svg' className='h-full w-full object-contain transition hover:opacity-90' alt='JOIN PAVILION' />
                </Button>
              </Link>
            )}

            {/* Download Button */}
            {!isMobileDevice() && (
              <>
                <SignedIn>
                  <Button
                    className='flex !h-auto h-full w-[189px] items-center justify-center bg-transparent !p-0 hover:bg-transparent focus:bg-transparent focus-visible:bg-transparent active:bg-transparent'
                    onClick={() => {
                      download();
                    }}
                  >
                    <img src='/DOWNLOAD.svg' className='h-full w-full object-contain transition hover:opacity-90' alt='Download' />
                  </Button>
                </SignedIn>
                <SignedOut>
                  <SignInButton mode='modal'>
                    <Button
                      className='flex !h-auto h-full w-[189px] items-center justify-center bg-transparent !p-0 hover:bg-transparent focus:bg-transparent focus-visible:bg-transparent active:bg-transparent'
                      onClick={handleDownloadClick}
                    >
                      <img
                        src='/DOWNLOAD.svg'
                        className='h-full w-full object-contain transition hover:opacity-90'
                        alt='Sign in to Download'
                      />
                    </Button>
                  </SignInButton>
                </SignedOut>
              </>
            )}

            {/* user button */}
            <SignedOut>
              <SignInButton mode='modal'>
                {/* Custom button content */}
                <button className='p-2 text-gray-300 hover:text-white'>
                  <User size={24} />
                </button>
              </SignInButton>
            </SignedOut>
            <SignedIn>
              <UserButton
                appearance={{
                  elements: {
                    rootBox: 'items-center',
                    userButtonTrigger: 'h-12 focus:!h-12',
                    userButtonAvatarBox: 'size-12',
                  },
                }}
              >
                <UserButton.MenuItems>
                  <UserButton.Link
                    label='My Profile'
                    labelIcon={<FontAwesomeIcon icon={faUser} size='sm' />}
                    href={profileUrl || `/${ownerAliasPlatformPrefix}/${ownerAlias}`}
                  />
                  <UserButton.Link
                    label='Remote Dashboard'
                    labelIcon={<FontAwesomeIcon icon={faComputer} size='sm' />}
                    href={constants.DASHBOARD_BASE_URL}
                  />
                </UserButton.MenuItems>
                <UserButton.UserProfilePage
                  label='Privacy'
                  url='privacy'
                  labelIcon={
                    <div>
                      <FontAwesomeIcon icon={faLock} className='text-md' />
                    </div>
                  }
                >
                  <div className='h-100 flex flex-col gap-y-2'>
                    <Privacy edition={'pavilion'} />
                  </div>
                </UserButton.UserProfilePage>
              </UserButton>
            </SignedIn>
          </div>
        </nav>
      </header>
      {/* Mobile Pavilion header */}
      <header className='text-header-nav sticky top-0 z-40 m-0 flex w-full border-b-2 border-gray-700 bg-gray-900 px-4 pt-2 align-middle md:hidden'>
        {/* Left section: Menu and Logo */}
        <div className='flex items-center gap-x-2'>
          <nav id='mobile menu' className='flex justify-start p-2 align-middle'>
            <label className='relative z-40 cursor-pointer px-3 py-6' htmlFor='mobile-menu'>
              <input className='peer hidden' type='checkbox' id='mobile-menu' />
              <div className="relative z-50 block h-[1px] w-7 bg-transparent bg-white content-[''] before:absolute before:top-[-0.35rem] before:z-50 before:block before:h-full before:w-full before:bg-white before:transition-all before:duration-200 before:ease-out before:content-[''] after:absolute after:bottom-[-0.35rem] after:left-0 after:block after:h-full after:w-full after:bg-white after:transition-all after:duration-200 after:ease-out after:content-[''] peer-checked:bg-transparent before:peer-checked:top-0 before:peer-checked:w-full before:peer-checked:rotate-45 before:peer-checked:transform after:peer-checked:bottom-0 after:peer-checked:w-full after:peer-checked:-rotate-45 after:peer-checked:transform"></div>
              <div className='fixed inset-0 z-40 hidden h-full w-full bg-black/50 backdrop-blur-sm peer-checked:block'>&nbsp;</div>
              <div className='fixed left-0 top-0 z-40 h-full w-full -translate-x-full overflow-y-auto overscroll-y-none transition duration-500 peer-checked:translate-x-0'>
                <div id='mobile-menu-links' className='float-left min-h-full w-[65%] bg-gray-900 px-6 pt-12 shadow-2xl'>
                  <menu>
                    <ul className='flex h-min flex-col gap-y-8 py-12 align-middle text-2xl'>
                      {links.map((link) => (
                        <Link key={link.url} to={link.url}>
                          <li className='flex-row hover:text-white focus:text-white active:text-gray-400'>{link.name}</li>
                        </Link>
                      ))}
                    </ul>
                  </menu>
                </div>
              </div>
            </label>
          </nav>
        </div>

        {/* Center section: Play Button */}
        <div className='flex flex-1 items-center justify-center'>
          {!isOnProfilePage ? (
            <Link to={`${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}/p/playcast/`}>
              <Button className='flex !h-auto h-full w-[180px] items-center justify-center bg-transparent !p-0 hover:bg-transparent focus:bg-transparent focus-visible:bg-transparent active:bg-transparent'>
                <img src='/PLAY NOW.svg' className='h-full w-full object-contain transition hover:opacity-90' alt='PLAY' />
              </Button>
            </Link>
          ) : (
            <Link to={`${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}/a/Playcast`}>
              <Button className='flex !h-auto h-full w-[180px] items-center justify-center bg-transparent !p-0 hover:bg-transparent focus:bg-transparent focus-visible:bg-transparent active:bg-transparent'>
                <img src='/JOIN PAVILION.svg' className='h-full w-full object-contain transition hover:opacity-90' alt='JOIN PAVILION' />
              </Button>
            </Link>
          )}
        </div>

        {/* Right section: User Button */}
        <div className='flex items-center p-2'>
          <div className='px-3'>
            <SignedOut>
              <SignInButton mode='modal'>
                <button className='p-2 text-gray-300 hover:text-white'>
                  <User size={24} />
                </button>
              </SignInButton>
            </SignedOut>
            <SignedIn>
              <UserButton
                appearance={{
                  elements: {
                    userButtonTrigger: 'h-10 focus:!h-10',
                    userButtonAvatarBox: 'size-7',
                  },
                }}
              >
                <UserButton.MenuItems>
                  <UserButton.Link
                    label='My Profile'
                    labelIcon={<FontAwesomeIcon icon={faUser} size='sm' />}
                    href={profileUrl || `/${ownerAliasPlatformPrefix}/${ownerAlias}`}
                  />
                  <UserButton.Link
                    label='Remote Dashboard'
                    labelIcon={<FontAwesomeIcon icon={faComputer} className='text-md' />}
                    href={constants.DASHBOARD_BASE_URL}
                  />
                </UserButton.MenuItems>
                <UserButton.UserProfilePage
                  label='Privacy'
                  url='privacy'
                  labelIcon={<FontAwesomeIcon icon={faUserCog} className='text-2xl' />}
                >
                  <div className='flex flex-col gap-y-2'>
                    <Privacy edition={'pavilion'} />
                  </div>
                </UserButton.UserProfilePage>
              </UserButton>
            </SignedIn>
          </div>
        </div>
      </header>
    </>
  ) : (
    <>
      {/* Desktop header */}
      <header className='bg-header text-content-text sticky top-0 z-[99] m-0 hidden w-full border-b-2 border-purple-900 px-4 pt-2 align-middle md:flex'>
        <Link to='/' id='logo' className='mr-2 flex flex-row gap-x-2 p-2 align-middle lg:mr-16'>
          <img src='/images/playcast_logo-96x96.png' alt='Playcast interactive game streaming platform banner' className='h-12 w-12' />
          <h1 className='text-content-text-headline font-headline text-3xl font-bold md:pt-2'>
            Playcast
          </h1>
        </Link>

        <nav id='large menu' className='flex flex-grow gap-x-4 p-2 align-middle'>
          <ul className='flex h-min flex-row align-middle md:gap-x-4 md:pt-3.5 md:text-lg lg:gap-x-8 lg:pt-3 lg:text-xl xl:gap-x-10'>
            {links.map((link) => (
              <li key={link.name} className='hover:text-primary-hover focus:text-primary-hover active:text-primary-active'>
                <Link to={link.url}>{link.name}</Link>
              </li>
            ))}
          </ul>

          <div className='ml-auto mr-8 flex gap-x-4'>
            {/* {!isMobileDevice() && (
              <Button className='flex space-x-2 rounded-full bg-amber-400' onClick={() => download(isPavilionMode)}>
                <FontAwesomeIcon icon={faWindows} className='text-2xl' />
                <span>Download</span>
              </Button>
            )} */}
            <SignedOut>
              <SignInButton mode='modal'>
                {/* Custom button content */}
                <button className='p-2 text-gray-300 hover:text-white'>
                  <User size={24} />
                </button>
              </SignInButton>
            </SignedOut>
            <SignedIn>
              <UserButton
                appearance={{
                  elements: {
                    rootBox: 'items-center',
                    userButtonTrigger: 'h-8 focus:!h-8',
                    userButtonAvatarBox: 'size-8',
                  },
                }}
              >
                <UserButton.MenuItems>
                  <UserButton.Link
                    label='My Profile'
                    labelIcon={<FontAwesomeIcon icon={faUser} size='sm' />}
                    href={profileUrl || `/${ownerAliasPlatformPrefix}/${ownerAlias}`}
                  />
                  <UserButton.Link
                    label='Remote Dashboard'
                    labelIcon={<FontAwesomeIcon icon={faComputer} size='sm' />}
                    href={constants.DASHBOARD_BASE_URL}
                  />
                </UserButton.MenuItems>
                <UserButton.UserProfilePage
                  label='Privacy'
                  url='privacy'
                  labelIcon={
                    <div>
                      <FontAwesomeIcon icon={faLock} className='text-md' />
                    </div>
                  }
                >
                  <div className='h-100 flex flex-col gap-y-2'>
                    <Privacy edition={'pavilion'} />
                  </div>
                </UserButton.UserProfilePage>
              </UserButton>
            </SignedIn>
          </div>
        </nav>
      </header>

      {/* Mobile header */}
      <header className='bg-header text-content-text sticky top-0 z-40 m-0 flex w-full border-b-2 border-purple-900 px-4 pt-2 align-middle md:hidden'>
        {/* Left section: Menu and Logo */}
        <div className='flex items-center gap-x-2'>
          <nav id='mobile menu' className='flex justify-start p-2 align-middle'>
            <label className='relative z-40 cursor-pointer px-3 py-6' htmlFor='mobile-menu'>
              <input className='peer hidden' type='checkbox' id='mobile-menu' />
              <div className="relative z-50 block h-[1px] w-7 bg-transparent bg-white content-[''] before:absolute before:top-[-0.35rem] before:z-50 before:block before:h-full before:w-full before:bg-white before:transition-all before:duration-200 before:ease-out before:content-[''] after:absolute after:bottom-[-0.35rem] after:left-0 after:block after:h-full after:w-full after:bg-white after:transition-all after:duration-200 after:ease-out after:content-[''] peer-checked:bg-transparent before:peer-checked:top-0 before:peer-checked:w-full before:peer-checked:rotate-45 before:peer-checked:transform after:peer-checked:bottom-0 after:peer-checked:w-full after:peer-checked:-rotate-45 after:peer-checked:transform"></div>
              <div className='fixed inset-0 z-40 hidden h-full w-full bg-black/50 backdrop-blur-sm peer-checked:block'>&nbsp;</div>
              <div className='fixed left-0 top-0 z-40 h-full w-full -translate-x-full overflow-y-auto overscroll-y-none transition duration-500 peer-checked:translate-x-0'>
                <div id='mobile-menu-links' className='float-left min-h-full w-[45%] bg-purple-900 px-6 pt-12 shadow-2xl'>
                  <menu>
                    <ul className='flex h-min flex-col gap-y-8 py-12 align-middle text-2xl'>
                      {links.map((link) => (
                        <Link key={link.url} to={link.url}>
                          <li className='hover:text-primary-hover focus:text-primary-hover active:text-primary-active flex-row'>
                            {link.name}
                          </li>
                        </Link>
                      ))}
                    </ul>
                  </menu>
                </div>
              </div>
            </label>
          </nav>
        </div>

        <Link to='/' id='mobile logo' className='flex flex-grow flex-row justify-center gap-x-2 pt-3 align-middle'>
          <img src='/images/playcast_logo-96x96.png' alt='Playcast interactive game streaming platform banner' className='h-10 w-10' />
          <h1 className='text-content-text-headline font-headline pt-1 text-2xl font-bold md:pt-2'>
            Playcast
          </h1>
        </Link>

        <div className='flex items-center p-2'>
          <div className='px-3'>
            <SignedOut>
              <SignInButton mode='modal'>
                {/* Custom button content */}
                <button className='p-2 text-gray-300 hover:text-white'>
                  <User size={24} />
                </button>
              </SignInButton>
            </SignedOut>
            <SignedIn>
              <UserButton
                appearance={{
                  elements: {
                    userButtonTrigger: 'h-10 focus:!h-10',
                    userButtonAvatarBox: 'size-7',
                  },
                }}
              >
                <UserButton.MenuItems>
                  <UserButton.Link
                    label='My Profile'
                    labelIcon={<FontAwesomeIcon icon={faUser} size='sm' />}
                    href={profileUrl || `/${ownerAliasPlatformPrefix}/${ownerAlias}`}
                  />
                  <UserButton.Link
                    label='Remote Dashboard'
                    labelIcon={<FontAwesomeIcon icon={faComputer} className='text-md' />}
                    href={constants.DASHBOARD_BASE_URL}
                  />
                </UserButton.MenuItems>
                <UserButton.UserProfilePage
                  label='Privacy'
                  url='privacy'
                  labelIcon={
                    <div>
                      <FontAwesomeIcon icon={faUserCog} className='text-2xl' />
                    </div>
                  }
                >
                  <div className='flex flex-col gap-y-2'>
                    <Privacy edition={'pavilion'} />
                  </div>
                </UserButton.UserProfilePage>
              </UserButton>
            </SignedIn>
          </div>
        </div>
      </header>
    </>
  );
};
